"use client";

import { createContext } from "@pharaday/utils";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import type { VariantProps } from "class-variance-authority";
import { type ComponentProps, useMemo } from "react";

import { cn } from "../../utils/cn.util";
import Skeleton from "../skeleton";
import { TextEllipsis } from "../text-ellipsis";
import { avatarStyles, avatarTextStyles } from "./styles";

export type AvatarRootProps = ComponentProps<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarStyles> & {
    isOnline?: boolean;
    mainText?: string;
    subText?: string;
    isLoading?: boolean;
  };

type AvatarContext = Pick<AvatarRootProps, "size" | "isOnline">;

const [useAvatarContext, AvatarContextProvider] = createContext<AvatarContext>();

export { useAvatarContext };

export const Root = ({
  ref,
  className,
  size = "sm",
  isOnline,
  mainText,
  subText,
  isLoading,
  ...props
}: AvatarRootProps) => {
  const providerValue = useMemo<AvatarContext>(() => ({ size, isOnline }), [size, isOnline]);

  const avatar = (
    <AvatarContextProvider value={providerValue}>
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          // NOTE: "avatar" class is used for avatar group css selector
          "avatar rounded-full focus:outline-hidden focus-visible:shadow-button-primary",
          "relative flex flex-col items-center justify-center text-center font-medium uppercase",
          avatarStyles({ size }),
          className
        )}
        tabIndex={0}
        {...props}
      />
    </AvatarContextProvider>
  );

  if (!isLoading && !mainText && !subText) {
    return avatar;
  }

  return (
    <div className={cn("flex w-full items-center overflow-hidden", avatarTextStyles({ size }))}>
      {avatar}
      {isLoading ? (
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      ) : (
        <div className="flex flex-1 flex-col truncate">
          {mainText && (
            <TextEllipsis content={mainText} asChild>
              <p className="main-text truncate font-bold text-neutral-10">{mainText}</p>
            </TextEllipsis>
          )}
          {subText && (
            <TextEllipsis content={mainText} asChild>
              <p className="sub-text truncate text-sm! font-normal text-neutral-60">{subText}</p>
            </TextEllipsis>
          )}
        </div>
      )}
    </div>
  );
};

Root.displayName = AvatarPrimitive.Root.displayName;
